import { Card, Margin, P, Strong, Notification } from '@ovotech/nebula';
import Big from 'big.js';
import React, { Fragment } from 'react';

import { LiveRegion, ProjectedBalanceChart } from '@/src/components';
import {
  ContractsResponse,
  CurrencyValueResponse,
  ProjectedCostsResponse,
} from '@/src/types/Response';
import {
  recDdForInDebt,
  recDdIsHigherThanMinimum,
} from '@/src/utils/shouldShowRecommendedDD';

import { formatCurrencyValue, formatPounds } from '@/src/utils/money';
import { formatFullDateWithoutOrdinal } from '@/src/utils/date';
import {
  StyledAdvicePanelFigures,
  StyledBr,
  StyledIcon,
  StyledPredictableBalanceWrapper,
} from '../DirectDebitCalculator.styled';
import { DirectDebitCalculatorChartRadio } from '@/src/components/Chart/SettingsPanelRadio';
import { GraphType } from '../constants';
import { getChartData, getPredictableBalance } from '../utils';

type Props = {
  balance: CurrencyValueResponse;
  projectedCosts: ProjectedCostsResponse;
  newDirectDebitAmount: string;
  payingRightAmount: boolean;
  calculationEndDate: Date;
  isStandardDDCustomer: boolean;
  selectedGraphType: GraphType;
  contracts: ContractsResponse;
  handleGraphTypeChange: (value: GraphType) => void;
};

type AdvicePanelProps = {
  newDirectDebit: CurrencyValueResponse;
  payingRightAmount: boolean;
  projectedCosts: ProjectedCostsResponse;
  predictedBalance: Big;
  calculationEndDate: Date;
  isStandardDDCustomer: boolean;
};

const AdvicePanel = ({
  predictedBalance,
  newDirectDebit,
  projectedCosts: { recommendedDirectDebit },
  payingRightAmount,
  calculationEndDate,
  isStandardDDCustomer,
}: AdvicePanelProps) => {
  const endOfContractYearBalance = recDdForInDebt(
    isStandardDDCustomer,
    recommendedDirectDebit.friendlyAmount,
  );

  const inDebt =
    predictedBalance &&
    Math.round(Number(predictedBalance)) < endOfContractYearBalance
      ? Math.round(Number(predictedBalance)) < endOfContractYearBalance
      : false;

  const endDate = formatFullDateWithoutOrdinal(calculationEndDate);

  const liveContent = (
    <>
      <StyledPredictableBalanceWrapper>
        <P style={{ fontWeight: 'normal' }}>Monthly payment:</P>
        <StyledAdvicePanelFigures>
          {formatPounds(newDirectDebit)}
        </StyledAdvicePanelFigures>
        {inDebt ? (
          <StyledIcon name="info" color="#AE072C" />
        ) : (
          <StyledIcon name="check-circle" color="#1F3BCD" />
        )}
      </StyledPredictableBalanceWrapper>
      <StyledPredictableBalanceWrapper>
        <P style={{ fontWeight: 'normal' }}>
          Predicted balance by <StyledBr />
          {endDate}:
        </P>
        <StyledAdvicePanelFigures>
          {predictedBalance.gte(0)
            ? `£${predictedBalance}`
            : `-£${predictedBalance.abs()}`}
        </StyledAdvicePanelFigures>
      </StyledPredictableBalanceWrapper>
    </>
  );

  const statusList = recommendedDirectDebit.state || [];

  if (statusList.includes('AccountInLoss')) {
    return (
      <Notification
        id="advice-notification"
        variant={inDebt ? 'error' : 'info'}
      >
        <P>
          <Strong>
            Because you’re switching away, we’re not able to show you a payment
            recommendation.
          </Strong>
        </P>
      </Notification>
    );
  } else if (
    statusList.includes('BillingOnHold') ||
    statusList.includes('AmountOverSensibleUpperLimit') ||
    recommendedDirectDebit === null ||
    !predictedBalance
  ) {
    return (
      <Notification
        id="advice-notification"
        variant={inDebt ? 'error' : 'info'}
      >
        <P>
          <Strong>
            Sorry, we’re not able to show you a payment recommendation right
            now.
          </Strong>
        </P>
        <P>We’re working super hard to fix this.</P>
      </Notification>
    );
  } else if (statusList.includes('RecentlyOnboarded')) {
    return (
      <Notification
        id="advice-notification"
        variant={inDebt ? 'error' : 'info'}
      >
        <P>
          <Strong>
            Once you’ve been with us for 8 weeks, we can start showing you
            payment recommendations.
          </Strong>
        </P>
        <P>
          By that point, we should have a nice, clear picture of your monthly
          energy use.
        </P>
      </Notification>
    );
  } else {
    return (
      <Notification
        id="advice-notification"
        variant={inDebt ? 'error' : 'info'}
      >
        <LiveRegion>{liveContent}</LiveRegion>
        {liveContent}
        {payingRightAmount && !inDebt ? (
          <P>
            We think you’re paying the right amount for the energy you’re using.
          </P>
        ) : !recDdIsHigherThanMinimum(recommendedDirectDebit.friendlyAmount) ? (
          <P>
            We recommend you change your Direct Debit to <Strong>£5</Strong> –
            our minimum amount.
          </P>
        ) : inDebt ? (
          <P>
            <Strong>
              {formatCurrencyValue(
                { unit: 'GBP', dp: 2, ceil: true },
                recommendedDirectDebit.friendlyAmount!,
              )}
            </Strong>{' '}
            {isStandardDDCustomer
              ? `is the monthly minimum payment needed to make sure you have 1
            month's credit in your OVO account by
            ${endDate}.`
              : `is the monthly minimum payment needed to make sure there’s no balance left for you to pay in your OVO account by ${endDate}.`}
          </P>
        ) : null}
      </Notification>
    );
  }
};

export const ProjectedBalance = ({
  balance,
  projectedCosts,
  newDirectDebitAmount,
  payingRightAmount,
  calculationEndDate,
  isStandardDDCustomer,
  selectedGraphType,
  handleGraphTypeChange,
  contracts,
}: Props) => {
  const newDirectDebit: CurrencyValueResponse = {
    amount: newDirectDebitAmount || '0',
    currencyUnit: 'GBP',
  };

  const chartData = getChartData(balance, projectedCosts, newDirectDebit);

  const predictedBalance = getPredictableBalance(
    projectedCosts.recommendedDirectDebit,
    newDirectDebit,
    isStandardDDCustomer,
  );

  return (
    <Fragment>
      <Card>
        <AdvicePanel
          data-testid="dd-calc-projected-balance"
          newDirectDebit={newDirectDebit}
          projectedCosts={projectedCosts}
          predictedBalance={predictedBalance}
          payingRightAmount={payingRightAmount}
          calculationEndDate={calculationEndDate}
          isStandardDDCustomer={isStandardDDCustomer}
        />
        <Margin top={2}>
          <DirectDebitCalculatorChartRadio
            newDirectDebitAmount={newDirectDebit.amount}
            contracts={contracts}
            selectedGraphType={selectedGraphType}
            handleGraphTypeChange={handleGraphTypeChange}
          />
          <ProjectedBalanceChart data={chartData} />
        </Margin>
      </Card>
    </Fragment>
  );
};
